import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import BackendStatus from '../components/BackendStatus';
import BlockButton from '../components/BlockButton';
import config from '../config/config';

import { Heart, Users, Lightbulb, Shield, Camera, Mail, User } from 'lucide-react';

const Home = () => {
  const { user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [apiStatus, setApiStatus] = useState('checking');
  const [showBackendStatus, setShowBackendStatus] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState(new Set());

  // Cargar usuarios en línea desde el nuevo endpoint dedicado
  useEffect(() => {
    const loadOnlineUsers = async () => {
      try {
        setApiStatus('checking');
        // Usar el nuevo endpoint específico para usuarios en línea
        const response = await apiService.getOnlineUsers();
        
        // Verificar que la respuesta tenga usuarios
        if (response && response.users && response.users.length > 0) {
          // Transformar resultados al formato del frontend
          // El backend ya devuelve los datos en formato camelCase con edad calculada
          const transformedUsers = response.users.map(user => {
            const gender = user.sex === 'male' ? 'Hombre' : 'Mujer';
            const hasPhoto = !!(user.profilePicture || user.profile_picture);
            
            return {
              id: user.id,
              username: user.username,
              email: user.email,
              name: user.firstName || user.username || 'Usuario',
              age: user.age, // Ya viene calculado del backend
              gender: gender,
              state: user.location || 'Ciudad de México',
              municipio: user.municipio || '',
              description: user.bio || '',
              hasPhoto: hasPhoto,
              photoUrl: hasPhoto ? user.profilePicture : config.getDefaultAvatar(gender),
              isOnline: user.isOnline === true || user.isOnline === 1,
              lastSeen: user.lastLogin,
              // Nuevos campos del backend
              estadoCivil: user.estadoCivil,
              sexoBusca: user.sexoBusca,
              descripcionFisica: user.descripcionFisica,
              educacion: user.educacion,
              ocupacion: user.ocupacion,
              tipoPersonaBusca: user.tipoPersonaBusca,
              matchSex: user.matchSex || []
            };
          });
          
          setOnlineUsers(transformedUsers);
          setApiStatus('connected');
        } else {
          // No hay usuarios en línea
          setOnlineUsers([]);
          setApiStatus('connected');
        }
      } catch (error) {
        console.error('Error al cargar usuarios en línea:', error);
        setApiStatus('error');
        // No mostrar datos mock, solo lista vacía
        setOnlineUsers([]);
      }
    };

    loadOnlineUsers();
  }, []);

  // Manejar cambios de bloqueo
  const handleBlockChange = (userId, isBlocked) => {
    if (isBlocked) {
      setBlockedUsers(prev => new Set([...prev, userId]));
      // Remover el usuario de la lista de usuarios en línea
      setOnlineUsers(prev => prev.filter(user => user.id !== userId));
    } else {
      setBlockedUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary to-red-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <Heart className="h-12 w-12" />
                <h1 className="text-4xl md:text-5xl font-bold">SolterosMexico</h1>
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold mb-6">
                Encuentra tu pareja ideal en México
              </h2>
              
          
              
              {/* Botones de acción */}
              <div className="flex flex-col sm:flex-row gap-4">
                {user ? (
                  <>
                    <Link 
                      to="/mi-cuenta" 
                      className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 hover:scale-105 active:scale-95 transition-all duration-200 text-center shadow-lg hover:shadow-xl"
                    >
                      Mi Cuenta
                    </Link>
                    <Link 
                      to="/buscar-pareja" 
                      className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary hover:scale-105 active:scale-95 transition-all duration-200 text-center shadow-lg hover:shadow-xl"
                    >
                      Explorar Usuarios
                    </Link>
                  </>
                ) : (
                  <>
                    <Link 
                      to="/mi-cuenta" 
                      className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 hover:scale-105 active:scale-95 transition-all duration-200 text-center shadow-lg hover:shadow-xl"
                    >
                      Iniciar Sesión
                    </Link>
                    <Link 
                      to="/mi-cuenta"
                      state={{ mode: "register" }}
                      className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary hover:scale-105 active:scale-95 transition-all duration-200 text-center shadow-lg hover:shadow-xl"
                    >
                      Registrarte
                    </Link>

                  </>
                )}
              </div>
              
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=600&h=400&fit=crop"
                alt="Pareja feliz"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Diagnóstico del Backend */}
      {showBackendStatus && (
        <section className="py-8 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <BackendStatus />
          </div>
        </section>
      )}

      {/* Usuarios en línea */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-text mb-4 flex items-center justify-center space-x-2">
              <Users className="h-8 w-8 text-primary" />
              <span>Usuarios en línea</span>
            </h3>
            <p className="text-gray-600 text-lg">Conoce a personas que están buscando el amor</p>
          </div>
          
          {onlineUsers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {onlineUsers.map((displayUser) => (
                <Link 
                  key={displayUser.id} 
                  to={`/perfil/${displayUser.id}`}
                  className="card hover:shadow-lg hover:scale-105 transition-all duration-300 transform cursor-pointer group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <img
                        src={displayUser.photoUrl}
                        alt={displayUser.name}
                        className="w-12 h-12 rounded-full object-cover group-hover:scale-110 transition-transform duration-200"
                      />
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-text group-hover:text-primary transition-colors duration-200">{displayUser.name}</h4>
                      <p className="text-sm text-gray-600">{displayUser.age} años • {displayUser.state}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">
                {apiStatus === 'error' 
                  ? 'No se pudieron cargar los usuarios en línea. Intenta más tarde.' 
                  : 'No hay usuarios en línea en este momento.'}
              </p>
            </div>
          )}

          <div className="text-center">
            <Link to="/buscar-pareja" className="btn-primary hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg">
              Ver más usuarios
            </Link>
          </div>
        </div>
      </section>


      {/* Tips para conseguir pareja */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-text mb-4 flex items-center justify-center space-x-2">
              <Lightbulb className="h-8 w-8 text-primary" />
              <span>Tips para conseguir pareja</span>
            </h3>
            <p className="text-gray-600 text-lg">Consejos útiles para tener éxito en el amor en línea</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Tip #1 */}
            <div className="card hover:shadow-lg hover:scale-105 transition-all duration-300 transform cursor-pointer group">
              <div className="flex items-start space-x-3">
                <div className="bg-primary text-white rounded-full p-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-primary mb-2 group-hover:text-red-600 transition-colors duration-200">Tip #1</h4>
                  <h5 className="font-semibold text-text mb-2">No esperes sentado...</h5>
                  <p className="text-gray-600 text-sm">
                    Si lo que quieres es buscar pareja... Abre tu cuenta gratuita, busca a las personas que te interesan, 
                    y en cada perfil encontrarás un botón de Contactar para entablar una comunicación.
                  </p>
                </div>
              </div>
            </div>

            {/* Tip #2 */}
            <div className="card hover:shadow-lg hover:scale-105 transition-all duration-300 transform cursor-pointer group">
              <div className="flex items-start space-x-3">
                <div className="bg-primary text-white rounded-full p-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
                  <Camera className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-primary mb-2 group-hover:text-red-600 transition-colors duration-200">Tip #2</h4>
                  <h5 className="font-semibold text-text mb-2">Ponle foto a tu perfil</h5>
                  <p className="text-gray-600 text-sm">
                    Es más probable que alguien conteste tus mensajes si tienes una foto en tu perfil. 
                    Así es el amor en línea.
                  </p>
                </div>
              </div>
            </div>

            {/* Tip #3 */}
            <div className="card hover:shadow-lg hover:scale-105 transition-all duration-300 transform cursor-pointer group">
              <div className="flex items-start space-x-3">
                <div className="bg-primary text-white rounded-full p-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
                  <Heart className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-primary mb-2 group-hover:text-red-600 transition-colors duration-200">Tip #3</h4>
                  <h5 className="font-semibold text-text mb-2">Muéstrate interesante</h5>
                  <p className="text-gray-600 text-sm">
                    Que tus mensajes reflejen que eres respetuoso, interesante y accesible.
                  </p>
                </div>
              </div>
            </div>

            {/* Tip #4 */}
            <div className="card hover:shadow-lg hover:scale-105 transition-all duration-300 transform cursor-pointer group">
              <div className="flex items-start space-x-3">
                <div className="bg-primary text-white rounded-full p-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-primary mb-2 group-hover:text-red-600 transition-colors duration-200">Tip #4</h4>
                  <h5 className="font-semibold text-text mb-2">Envía mensajes personales</h5>
                  <p className="text-gray-600 text-sm">
                    Contacta a las personas cuyo perfil te ha interesado y siempre menciona por qué decidiste 
                    contactarlo(a) al buscar el amor en línea.
                  </p>
                </div>
              </div>
            </div>

            {/* Tip #5 */}
            <div className="card hover:shadow-lg hover:scale-105 transition-all duration-300 transform cursor-pointer group">
              <div className="flex items-start space-x-3">
                <div className="bg-primary text-white rounded-full p-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-primary mb-2 group-hover:text-red-600 transition-colors duration-200">Tip #5</h4>
                  <h5 className="font-semibold text-text mb-2">Ten en cuenta tu seguridad</h5>
                  <p className="text-gray-600 text-sm">
                    Si conociste a alguien, tómate tu tiempo antes de una invitación a salir o proporcionar 
                    datos personales. Así podrás buscar pareja de manera segura.
                  </p>
                </div>
              </div>
            </div>

            {/* Anuncio integrado */}
            <div className="card bg-gradient-to-br from-yellow-400 to-orange-500 text-white hover:shadow-lg hover:scale-105 transition-all duration-300 transform cursor-pointer group">
              <div className="text-center">
                <div className="bg-white bg-opacity-20 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <Heart className="h-6 w-6" />
                </div>
                <h4 className="font-bold text-lg mb-2">¡Encuentra el amor!</h4>
                <p className="text-sm mb-4 opacity-90">
                  Miles de personas esperan conocerte. ¡Únete ahora!
                </p>
                <Link
                  to="/mi-cuenta"
                  state={{ mode: "register" }}
                  className="bg-white text-orange-600 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-gray-100 hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Registrarme
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Anuncio Banner Inferior */}
      <section className="py-8 bg-gradient-to-r from-pink-500 to-red-500">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between text-white">
            <div className="mb-4 md:mb-0">
              <h3 className="text-2xl font-bold mb-2">¿Listo para encontrar tu media naranja?</h3>
              <p className="text-lg opacity-90">Únete a la comunidad de solteros más grande de México</p>
            </div>
                         <div className="flex space-x-4">
            <Link
              to="/mi-cuenta"
              state={{ mode: "register" }}
              className="bg-white text-pink-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Crear cuenta gratis
            </Link>
               <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-pink-600 hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg">
                 Más información
               </button>
             </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
